import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal, Label } from "@/components/ui-bits";
import { ResultsBoard, AddResultForm } from "@/components/ResultsBoard";
import { ResultInsights, MonitoringCard } from "@/components/PortalInsights";
import DoseDiary from "@/components/DoseDiary";
import ShareWithGp from "@/components/ShareWithGp";
import { FileDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function PdfButton({ path, label = "Download PDF for your GP" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api${path}`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || "androgen-health-results.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={download} disabled={busy} className="btn-ghost !py-2 !px-4 text-xs gap-2" data-testid="pdf-download">
      <FileDown size={14} /> {busy ? "Preparing…" : label}
    </button>
  );
}
import { api, useAuth } from "@/lib/auth";

function PortalShell({ title, lede, children }) {
  const { user, logout } = useAuth();
  return (
    <section className="pt-32 sm:pt-40 pb-24 px-5 sm:px-8" data-testid="portal-shell">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Label>Patient portal</Label>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mt-4">{title}</h1>
            {lede && <p className="text-mute mt-3 max-w-2xl">{lede}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-mute" data-testid="portal-user-email">{user?.email}</span>
            {user?.role === "admin" && <Link to="/portal/admin" className="btn-ghost !py-2 !px-4 text-xs" data-testid="portal-admin-link">Clinic admin</Link>}
            <Link to="/portal" className="btn-ghost !py-2 !px-4 text-xs" data-testid="portal-home-link">My results</Link>
            <button type="button" onClick={logout} className="text-mute hover:text-teal transition-colors" data-testid="portal-logout">Sign out</button>
          </div>
        </div>
        <div className="mt-12">{children}</div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [results, setResults] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [doses, setDoses] = useState([]);
  const [bookings, setBookings] = useState([]);

  const loadMonitoring = () => api("/monitoring").then(setMonitoring).catch(() => setMonitoring({}));

  useEffect(() => {
    api("/results").then((d) => setResults(d.results)).catch(() => setResults([]));
    api("/doses").then((d) => setDoses(d.doses)).catch(() => {});
    api("/bookings/mine").then((d) => setBookings(d.bookings)).catch(() => {});
    loadMonitoring();
  }, []);

  const remove = async (id) => {
    await api(`/results/${id}`, { method: "DELETE" });
    setResults((r) => r.filter((x) => x.result_id !== id));
    loadMonitoring();
  };

  return (
    <>
      <Seo title="My Results | Androgen Health Portal" description="Your blood results over time, explained." />
      <PortalShell title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}.`} lede="Each marker is shown against its reference range with a plain-English note. Results your clinic enters are marked verified; anything you add yourself is labelled self-entered.">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          {bookings[0] ? (
            <p className="text-sm text-mute" data-testid="upcoming-booking">Booked: <span className="text-bone/85">{bookings[0].product_name || "appointment"}</span>{bookings[0].booking_start ? <> · <span className="font-mono text-teal">{bookings[0].booking_start}</span></> : null} — preparation instructions were emailed to you.</p>
          ) : <span />}
          <PdfButton path="/results/export.pdf" />
        </div>
        <div className="grid lg:grid-cols-12 gap-6 items-start mb-10">
          <div className="lg:col-span-7">{monitoring !== null && <MonitoringCard monitoring={monitoring} planPath="/me/plan" onSaved={setMonitoring} />}</div>
          <div className="lg:col-span-5">{results !== null && <ResultInsights results={results} />}</div>
        </div>
        {results === null ? <p className="text-mute" data-testid="results-loading">Loading your results…</p> : (
          <Reveal><ResultsBoard results={results} doses={doses} onDelete={remove} canDelete={(r) => r.entered_by_role === "patient" || user?.role === "admin"} /></Reveal>
        )}
        <div className="mt-10"><DoseDiary doses={doses} setDoses={setDoses} /></div>
        <div className="mt-6"><ShareWithGp /></div>
        <div className="mt-10 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7">
            <AddResultForm path="/results" onAdded={(doc) => { setResults((r) => [...(r || []), doc].sort((a, b) => a.taken_at.localeCompare(b.taken_at))); loadMonitoring(); }} />
          </div>
          <aside className="lg:col-span-5 card-line rounded-2xl p-6 text-sm text-mute leading-relaxed space-y-3">
            <Label>Reading your results</Label>
            <p>Reference ranges shown are typical UK adult-male intervals; your laboratory's printed range takes precedence. A single result is a data point, not a diagnosis.</p>
            <p>Monitoring reminders follow your plan: Foundation at 3 then every 6 months; Precision and Specialist at 3 months then every 3 months.</p>
            <p>Questions about a result? <Link to="/book" className="text-teal hover:underline">Book a review</Link> or use <Link to="/learn" className="text-teal hover:underline">the knowledge centre</Link>.</p>
          </aside>
        </div>
      </PortalShell>
    </>
  );
}

export function AdminPage() {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);
  const [monitoring, setMonitoring] = useState(null);
  const [doses, setDoses] = useState([]);

  useEffect(() => {
    api("/admin/patients").then((d) => setPatients(d.patients)).catch(() => setPatients([]));
  }, []);

  const pick = async (p) => {
    setSelected(p);
    const [d, m, ds] = await Promise.all([api(`/admin/patients/${p.user_id}/results`), api(`/admin/patients/${p.user_id}/monitoring`), api(`/admin/patients/${p.user_id}/doses`)]);
    setResults(d.results);
    setMonitoring(m);
    setDoses(ds.doses);
  };

  const remove = async (id) => {
    await api(`/results/${id}`, { method: "DELETE" });
    setResults((r) => r.filter((x) => x.result_id !== id));
  };

  return (
    <>
      <Seo title="Clinic Admin | Androgen Health Portal" description="Enter verified blood results for patients." />
      <PortalShell title="Clinic admin." lede="Select a patient to view their results and enter verified values from the laboratory report.">
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-4 card-line rounded-2xl p-4" data-testid="admin-patient-list">
            <p className="mono-label text-dusk px-2 py-2">{patients.length} patients</p>
            <ul className="divide-y divide-line">
              {patients.map((p) => (
                <li key={p.user_id}>
                  <button type="button" onClick={() => pick(p)} className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${selected?.user_id === p.user_id ? "bg-teal/10 text-teal" : "hover:bg-white/[0.03]"}`} data-testid={`admin-patient-${p.user_id}`}>
                    <p className="text-sm">{p.name}</p>
                    <p className="text-xs text-dusk">{p.email}</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="mono-label text-teal" data-testid="admin-selected-patient">{selected.name} · {selected.email}</p>
                  <PdfButton path={`/admin/patients/${selected.user_id}/export.pdf`} label="Download results PDF" />
                </div>
                {monitoring && <MonitoringCard monitoring={monitoring} planPath={`/admin/patients/${selected.user_id}/plan`} onSaved={setMonitoring} />}
                <ResultInsights results={results} />
                <ResultsBoard results={results} doses={doses} onDelete={remove} canDelete={() => true} />
                <DoseDiary doses={doses} setDoses={setDoses} readOnly />
                <AddResultForm path={`/admin/patients/${selected.user_id}/results`} title={`Enter verified result for ${selected.name}`} onAdded={(doc) => { setResults((r) => [...r, doc].sort((a, b) => a.taken_at.localeCompare(b.taken_at))); api(`/admin/patients/${selected.user_id}/monitoring`).then(setMonitoring); }} />
              </>
            ) : <p className="text-mute" data-testid="admin-select-hint">Select a patient to begin.</p>}
          </div>
        </div>
      </PortalShell>
    </>
  );
}
